import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import { Button, PageHeader } from '@pushly/aqe/lib/components'
import { useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { AppState } from '../../stores/app'
import { AppService } from '../../services'
import { CatalogDataView } from './catalog-data-view'
import { CatalogItemType } from './types'
import { resolveInitialLocale, CATALOG_LOCALE_PREF_KEY } from './locale-utils'
import { ItemGroupsDataView } from './item-groups/item-groups-data-view'
import SimpleTabbedView from '../../components/simple-tabbed-view/simple-tabbed-view'
import './catalog.scss'
import { useService } from '@pushly/aqe/lib/hooks'
import { asCaslSubject, CurrentUserCan, getDemoAttributesFor } from '../../stores/app-ability'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { capitalizeFirstLetter } from 'src/_utils/string'

/**
 * CatalogPage component - Root page component for the Catalog Browser feature.
 *
 * Responsibilities:
 * - Read itemType from appState.currentDomain.ecommConfig.itemType
 * - Redirect to dashboard if itemType not configured
 * - Render page header with dynamic title: "{ItemType} Catalog"
 * - Render CatalogDataView in a tabbed layout for future Item Groups tab
 */
export const CatalogPage: React.FC = observer(() => {
    const appState = useService(AppState)
    const appService = useService(AppService)

    const domain = appState.currentDomain
    const itemType = domain?.ecommConfig?.itemType as CatalogItemType | undefined
    const catalogLocales = domain?.ecommConfig?.catalogLocales
    const catalogDefaultLocale = domain?.ecommConfig?.catalogDefaultLocale
    const hasMultipleLocales = !!catalogLocales && catalogLocales.length > 1

    const userPreferences = useUserPreferences(appState.currentUser?.id, domain?.id)

    const [selectedLocale, setSelectedLocale] = useState<string | undefined>(() => {
        if (!hasMultipleLocales || !catalogLocales) return undefined
        return resolveInitialLocale(catalogLocales, catalogDefaultLocale, userPreferences)
    })

    const handleLocaleChange = (locale: string) => {
        setSelectedLocale(locale)
        userPreferences.set([CATALOG_LOCALE_PREF_KEY], locale)
    }

    const [activeTab, setActiveTab] = useState<string | null>(null)

    useEffect(() => {
        if (domain && !itemType) {
            appService.routeWithinDomain('/dashboard')
        }
    }, [domain?.id, itemType, appService])

    if (!domain || !itemType) {
        return null
    }

    const itemTypeLabel = capitalizeFirstLetter(itemType)

    // Must be called unconditionally (before any early return) because
    // wrapComponentTab uses React.useMemo internally — a hook.
    const CatalogTab = SimpleTabbedView.wrapComponentTab(CatalogDataView, {
        tabName: null,
        tabLabel: 'Catalog',
    })

    const ItemGroupsTab = SimpleTabbedView.wrapComponentTab(ItemGroupsDataView, {
        tabName: 'item-groups',
        tabLabel: `${itemTypeLabel} Groups`,
    })

    const pageTitle = `${itemTypeLabel} Catalog`

    const localeProps = hasMultipleLocales
        ? {
              locale: selectedLocale,
              onLocaleChange: handleLocaleChange,
              catalogLocales,
          }
        : {}

    const catalogTabs = [
        {
            tabClassName: 'catalog-list-tab',
            component: CatalogTab,
            props: () => ({
                domainId: domain.id,
                itemType,
                ...localeProps,
            }),
        },
        {
            tabClassName: 'item-groups-tab',
            component: ItemGroupsTab,
            props: () => ({
                domainId: domain.id,
                itemType,
            }),
        },
    ]

    const createItemGroupPath = useMemo(
        () => appService.routeWithinDomain(`catalogs/item-groups/new`, true),
        [domain.id],
    )

    return (
        <div className="catalog-page">
            <PageHeader
                title={pageTitle}
                onTitleSet={appService.customizeTabTitle}
                action={
                    activeTab === 'item-groups' ? (
                        <CurrentUserCan
                            do={AbilityAction.CREATE}
                            on={asCaslSubject(SubjectEntity.CATALOG, {
                                domainId: domain.id,
                                ...getDemoAttributesFor(domain.id, appState.currentDomain),
                            })}
                        >
                            <Button
                                type="primary"
                                onClick={() => appService.route(createItemGroupPath)}
                                altHref={createItemGroupPath}
                            >
                                {`Create ${itemTypeLabel} Group`}
                            </Button>
                        </CurrentUserCan>
                    ) : null
                }
            />
            <SimpleTabbedView
                pathStyle="slash"
                animated={false}
                tabs={catalogTabs}
                onTabChange={(tab) => {
                    const activeTabKey = 'name' in tab ? tab.name : tab.component.tabName
                    setActiveTab(activeTabKey)
                }}
            />
        </div>
    )
})
